import React from "react";
import { Box, Typography } from "@mui/material";

export interface TabLayoutTab {
    label: string;
}

export interface TabLayoutProps {
    /** Step labels in order (e.g. Application Details, Documents Upload, …) */
    tabs: TabLayoutTab[] | string[];
    /** Zero-based index of the active step */
    activeIndex: number;
    /** Called when a step is clicked (optional; omit for display-only) */
    onTabChange?: (index: number) => void;
}

const normalizeTabs = (tabs: TabLayoutTab[] | string[]): TabLayoutTab[] =>
    tabs.map((t) => (typeof t === "string" ? { label: t } : t));

/**
 * Summary page tab bar: rounded white container with teal active underline.
 */
const TabLayout: React.FC<TabLayoutProps> = ({
    tabs,
    activeIndex,
    onTabChange,
}) => {
    const items = normalizeTabs(tabs);
    const n = items.length;
    if (n === 0) return null;

    const activeTeal = "#00B3B5";
    const inactiveLabel = "#6f6b7d";

    return (
        <Box
            role="tablist"
            aria-label="Application steps"
            sx={{
                paddingTop: "0",
                display: "flex",
                alignItems: "flex-end",
                width: "100%",
                minHeight: 56,
                borderRadius: "14px",
                bgcolor: "#fff",
                overflowX: "auto",
                overflowY: "hidden",
                boxSizing: "border-box",
                px: { xs: 3.5, sm: 5, md: 3 },
                pt: 1.5,
                pb: 0,
                boxShadow: "0 8px 24px rgba(47, 43, 61, 0.08)",
                "&::-webkit-scrollbar": {
                    display: "none",
                },
                scrollbarWidth: "none",
            }}
        >
            {items.map((tab, index) => {
                const isActive = index === activeIndex;
                const clickable = Boolean(onTabChange);

                return (
                    <Box
                        key={`${tab.label}-${index}`}
                        role="tab"
                        aria-selected={isActive}
                        tabIndex={clickable ? 0 : -1}
                        onClick={() => clickable && onTabChange?.(index)}
                        onKeyDown={(e) => {
                            if (!clickable) return;
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onTabChange?.(index);
                            }
                        }}
                        sx={{
                            color: "#6f6b7d",
                            fontSize: "0.95rem",
                            height: "56px",
                            position: "relative",
                            flex: "0 0 auto",
                            minWidth: 130,
                            bgcolor: "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            py: 0,
                            px: { xs: 1.5, sm: 2 },
                            cursor: clickable ? "pointer" : "default",
                            transition: "color 0.2s ease",
                            borderBottom: isActive
                                ? `3px solid ${activeTeal}`
                                : "3px solid transparent",
                            "&:focus-visible": clickable
                                ? { outline: "2px solid #00B3B5", outlineOffset: -2 }
                                : {},
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: isActive ? activeTeal : inactiveLabel,
                                lineHeight: "22px",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {tab.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

export default TabLayout;
